import { ListItem, Text } from '@change/ds-core-ui';
import { DoneIcon, FlagIcon, LocationPinIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core/react/i18n';

import type { LocationItem, LocationItemTypeEnum } from 'src/app/pages/search/shared/types';

type LocationResultsProps = {
	locations: readonly LocationItem[];
	isLoading: boolean;
	isError: boolean;
	selectLocation: (value: string, type: LocationItemTypeEnum) => void;
};

export const LocationResults: React.FC<LocationResultsProps> = ({ locations, isLoading, isError, selectLocation }) => {
	if (isError) {
		return (
			<div className="flex flex-col items-center px-1 py-10">
				<Text as="p" className="text-center text-typography-weak">
					<Translate value="corgi.search.filters.location.error" />
				</Text>
			</div>
		);
	}
	if (isLoading) {
		return (
			<div className="flex flex-col gap-2 pt-6" data-testid="location-results-loading">
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
				<ListItemLoader />
			</div>
		);
	}

	if (!locations.length) {
		return (
			<div className="flex flex-col items-center px-1 py-10">
				<Text as="p" className="text-typography-weak">
					<Translate value="corgi.search.filters.location.no_results" />
				</Text>
			</div>
		);
	}

	return (
		<div className="pt-6">
			<ul className="flex flex-col gap-2">
				{locations.map((location) => (
					<li key={location.label}>
						<ListItem
							className="w-full justify-start overflow-hidden text-ellipsis whitespace-nowrap px-0"
							label={<Text truncate>{location.label}</Text>}
							leadingIcon={<LocationIcon type={location.type} />}
							onClick={() => {
								selectLocation(location.value, location.type);
							}}
							trailingIcon={location.isRefined ? <DoneIcon /> : undefined}
						/>
					</li>
				))}
			</ul>
		</div>
	);
};

const LocationIcon: React.FC<{ type: LocationItemTypeEnum }> = ({ type }) => {
	if (type === 'city') {
		return <LocationPinIcon className="text-icon-weak" />;
	}
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (type === 'country') {
		return (
			<div className="flex justify-center pr-1 text-icon-weak">
				<FlagIcon size="small" />
			</div>
		);
	}
	return null;
};

const ListItemLoader: React.FC = () => {
	return (
		<div className="animate-pulse">
			<div className="h-8 w-full rounded-small bg-fill-press" />
		</div>
	);
};
